import React, { useCallback, useEffect, useRef } from 'react';
import { useToggle } from 'react-use';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Icon, Loader } from 'semantic-ui-react';

import { toggleSimulationFilterThunk } from '../../actions/Simulation/SimulationFiltersActions';
import { activeTabIndexSelector } from '../../reducers/EditorReducer/EditorSelector';
import { setInitialTutorialState, TutorialActions } from '../../reducers/TutorialReducer/TutorialReducer';
import { TutorialCheck, TutorialCheckResponse } from '../../shared/api/api';
import { addModifiers } from '../../shared/helpers';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { groupChecksByResult } from '../AlphaTestingStatus/TestingStatus/TestingStatus';

import { ExitTutorialModeConfirmationModal, ExitTutorialModeConfirmationModalHandle } from './ExitTutorialModeConfirmationModal';
import './AlphaTutorialResults.scss';
import analytics from '../../shared/services/analytics/analytics';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

const TUTORIAL_RESULTS = {
  PASS: 'PASS',
  FAIL: 'FAIL',
};

export interface AlphaTutorialResultsInnerProps {
  checks: TutorialCheck[];
  nextTutorialName: string;
  stepIndex: number;
}

export function AlphaTutorialResultsInner({ checks, nextTutorialName, stepIndex }: AlphaTutorialResultsInnerProps) {
  const history = useHistory();

  const dispatch = useAppDispatch();

  const activeTabIndex = useSelector(activeTabIndexSelector);

  const handleClickBackToTutorial = useCallback(() => {
    analytics.trackCustomEvent('tutorial_click_notification_back');
    dispatch(toggleSimulationFilterThunk('tutorial', activeTabIndex, history));
  }, [activeTabIndex]);

  const [isAllResultsShown, toggleIsAllResultsShown] = useToggle(false);

  const checksByResult = React.useMemo(() => groupChecksByResult(checks), [checks]);
  const isAllChecksPassed = checksByResult[TUTORIAL_RESULTS.PASS]?.length === checks.length;

  const submitCheckName = 'Submit an Alpha';
  const isFailedSubmissionCheck = !isAllChecksPassed && checks.some(check => check.name === submitCheckName);

  const exitConfirmationModalRef = useRef<ExitTutorialModeConfirmationModalHandle>(null);
  useEffect(() => {
    //update tutorialState to unlock new page
    if (isAllChecksPassed) {
      dispatch(setInitialTutorialState());
    }
  }, [isAllChecksPassed]);

  const handleClickExit = useCallback(() => {
    analytics.trackCustomEvent('tutorial_click_notification_exit');
    exitConfirmationModalRef.current?.open();
  }, []);

  const handleConfirmExit = useCallback(() => {
    analytics.trackCustomEvent('tutorial_click_notification_exit_confirm');
    dispatch(TutorialActions.disableTutorialMode());
  }, []);

  const handleCancelExit = useCallback(() => {
    analytics.trackCustomEvent('tutorial_click_notification_exit_cancel');
  }, []);

  const handleToggleShowAllResults = useCallback(() => {
    if (isAllResultsShown) {
      analytics.trackCustomEvent('tutorial_click_notification_show_failed');
    } else {
      analytics.trackCustomEvent('tutorial_click_notification_show_all');
    }
    toggleIsAllResultsShown();
  }, [isAllResultsShown]);

  const handleNext = useCallback(() => {
    analytics.trackCustomEvent('tutorial_click_notification_next');
    dispatch(TutorialActions.advanceTutorial(stepIndex));
    dispatch(toggleSimulationFilterThunk('tutorial', activeTabIndex, history));
  }, []);

  const displayedResults = isAllResultsShown ? Object.keys(checksByResult) : [TUTORIAL_RESULTS.FAIL];

  if (isAllChecksPassed) {
    return (
      <div className="tutorial-results">
        <p className="tutorial-results__congratulations">
          <FormattedMessageHelper id="tutorialNotification.congratulations" tagName={'strong'} />
        </p>
        <p>
          <FormattedMessageHelper id="tutorialNotification.canProceed" values={{ nextTutorialName }} />
        </p>
        <div className="tutorial-results__actions">
          <Button className="button--small" color="green" size="small" onClick={handleNext}>
            <FormattedMessageHelper id="tutorialNotification.nextTutorial" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="tutorial-results">
      <ExitTutorialModeConfirmationModal ref={exitConfirmationModalRef} onConfirmExit={handleConfirmExit} onCancelExit={handleCancelExit} />

      {!isFailedSubmissionCheck && (
        <div className="tutorial-results__details">
          <div className="tutorial-results__checks-summary">
            <p>
              <FormattedMessageHelper id="tutorialNotification.checks.unmet" />
            </p>
            <button className="tutorial-results__toggle" onClick={handleToggleShowAllResults}>
              <FormattedMessageHelper
                id={isAllResultsShown ? 'tutorialNotification.checks.showFailChecksOnly' : 'tutorialNotification.checks.showAll'}
              />
            </button>
          </div>

          <ul className="tutorial-results__checks">
            {displayedResults?.map(result =>
              checksByResult[result].map(check => (
                <div key={check.name} className={addModifiers('tutorial-results__check', [result])}>
                  {result === TUTORIAL_RESULTS.FAIL ? <Icon name="arrow right" /> : '✔'} {check.name}
                </div>
              ))
            )}
          </ul>
        </div>
      )}

      <p>
        {!isFailedSubmissionCheck ? (
          <FormattedMessageHelper id="tutorialNotification.canGoBack" />
        ) : (
          <FormattedMessageHelper id="tutorialNotification.submitIfYouCan" />
        )}
      </p>

      <div className="tutorial-results__actions">
        <Button className="button--small" color="blue" size="small" onClick={handleClickBackToTutorial}>
          <FormattedMessageHelper id="tutorialNotification.backToTutorial" />
        </Button>
        <Button
          className="tutorial-results__exit-button button--small"
          size="small"
          onClick={handleClickExit}
          data-testid="exit-tutorial-mode-btn"
        >
          <FormattedMessageHelper id="tutorialNotification.exitTutorialMode" />
        </Button>
      </div>
    </div>
  );
}

export interface AlphaTutorialResultsProps {
  nextTutorialName: string;
  isFetching: boolean;
  checkResponse?: TutorialCheckResponse;
  stepIndex: number;
}

export function AlphaTutorialResults({ checkResponse, nextTutorialName, isFetching, stepIndex }: AlphaTutorialResultsProps) {
  const checks = checkResponse?.checks;

  if (isFetching || !checks) {
    return (
      <div className="tutorial-results__loader">
        <Loader active={true} inline="centered" size="big" />
      </div>
    );
  }

  return <AlphaTutorialResultsInner checks={checks} nextTutorialName={nextTutorialName} stepIndex={stepIndex} />;
}
